// src/components/ResultCard.js
import React, { useState } from 'react';
import { getStateStatus } from '../utils/picolApi';
import { getCriteriaLabels } from '../data/hhpData';
import { getGrapeStatus } from '../utils/grapeIndex';

export default function ResultCard({ product }) {
  const [expanded, setExpanded] = useState(false);
  const stateStatus  = getStateStatus([product]);
  const grapeStatus  = getGrapeStatus(product.Epa);
  const { hhpSummary } = product;

  const hasProhibited = hhpSummary?.prohibited?.length > 0;
  const hasWatchlist  = hhpSummary?.watchlist?.length > 0;
  const waRegistered  = stateStatus.washington.registered;
  const orRegistered  = stateStatus.oregon.registered;

  return (
    <div className={`result-card ${hasProhibited ? 'card-prohibited' : hasWatchlist ? 'card-watchlist' : 'card-clear'}`}>
      <div className="card-main" onClick={() => setExpanded(!expanded)}>
        <div className="card-left">
          <div className="card-name">{product.Name}</div>
          <div className="card-meta">
            <span className="card-epa">EPA #{product.Epa}</span>
            {product.Registrant?.Name && (
              <span className="card-registrant">{product.Registrant.Name}</span>
            )}
            {product.PesticideTypes?.length > 0 && (
              <span className="card-type">{product.PesticideTypes.map(t => t.Name).join(' / ')}</span>
            )}
          </div>
        </div>
        <div className="card-right">
          {/* State registration */}
          <div className="card-state-row">
            <span className={`mini-chip ${waRegistered ? 'chip-yes' : 'chip-no'}`}>
              WA {waRegistered ? '✓' : '✗'}
            </span>
            <span className={`mini-chip ${orRegistered ? 'chip-yes' : 'chip-no'}`}>
              OR {orRegistered ? '✓' : '✗'}
            </span>
          </div>
          {/* Grape labeling */}
          {grapeStatus.indexed && (
            <div className="card-grape-row">
              <GrapeChip state="WA" labeled={grapeStatus.wa} />
              <GrapeChip state="OR" labeled={grapeStatus.or} />
            </div>
          )}
          {/* HHP status */}
          <div className="card-hhp-row">
            {hasProhibited && <span className="mini-badge badge-prohibited-sm">PROHIBITED</span>}
            {!hasProhibited && hasWatchlist && <span className="mini-badge badge-watchlist-sm">WATCH LIST</span>}
            {!hasProhibited && !hasWatchlist && <span className="mini-badge badge-clear-sm">CLEAR</span>}
          </div>
          <button className="expand-btn">{expanded ? '▲ Less' : '▼ Details'}</button>
        </div>
      </div>

      {expanded && (
        <div className="card-details">
          {/* State not registered warnings */}
          {!waRegistered && (
            <div className="card-state-warning">
              ⛔ <strong>Not legal for use in Washington State</strong> — this product is not registered in WA in PICOL.
            </div>
          )}
          {!orRegistered && (
            <div className="card-state-warning">
              ⛔ <strong>Not legal for use in Oregon</strong> — this product is not registered in OR in PICOL.
            </div>
          )}

          {/* Grape labeling detail */}
          {grapeStatus.indexed && (
            <div className="card-grape-detail">
              <div className="detail-label">Grape Labeling Status</div>
              <div className="grape-detail-row">
                <GrapeDetailBadge state="Washington" labeled={grapeStatus.wa} />
                <GrapeDetailBadge state="Oregon" labeled={grapeStatus.or} />
              </div>
              <p className="grape-disclaimer">
                Always verify the current product label before applying to grapes.
                Label status is based on PICOL data and may not reflect the most recent registration changes.
              </p>
            </div>
          )}

          {/* Ingredients */}
          <div className="detail-section">
            <div className="detail-label">Active Ingredients</div>
            <div className="detail-ingredients">
              {(product.Ingredients || []).map(ing => {
                const match = hhpSummary?.all?.find(r => r.originalName === ing.Name);
                return (
                  <div key={ing.Id} className={`detail-ing ${
                    match?.status === 'prohibited' ? 'ing-prohibited' :
                    match?.status === 'watchlist'  ? 'ing-watchlist'  : 'ing-clear'
                  }`}>
                    <span className="detail-ing-dot" />
                    <span className="detail-ing-name">{ing.Name}</span>
                    {match?.status === 'prohibited' && <span className="detail-ing-flag">PROHIBITED</span>}
                    {match?.status === 'watchlist'  && <span className="detail-ing-flag watchlist-flag">WATCH LIST</span>}
                    {ing.Resistance?.MethodOfAction && (
                      <span className="detail-ing-moa">{ing.Resistance.MethodOfAction}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* State registrations */}
          <div className="detail-section">
            <div className="detail-label">State Registrations</div>
            <div className="detail-states">
              {(product.StateRecords || []).map((sr, i) => (
                <div key={i} className="detail-state-row">
                  <span className="detail-state-name">{sr.Name}</span>
                  <span className="detail-state-year">Year: {sr.Year}</span>
                  {sr.AgencyId && sr.AgencyId !== 'None' && (
                    <span className="detail-state-agency">Agency ID: {sr.AgencyId}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Other info */}
          <div className="detail-section detail-misc">
            {product.Formulation && <span className="detail-pill">Formulation: {product.Formulation}</span>}
            {product.SignalWord && (
              <span className={`detail-pill signal-${product.SignalWord}`}>
                Signal Word: {
                  product.SignalWord === 'D' ? 'DANGER' :
                  product.SignalWord === 'W' ? 'WARNING' :
                  product.SignalWord === 'C' ? 'CAUTION' : product.SignalWord
                }
              </span>
            )}
            {product.Organic && <span className="detail-pill organic-pill">ORGANIC</span>}
          </div>

          {product.SupplementalName && (
            <div className="detail-section">
              <div className="detail-label">Supplemental Label</div>
              <div className="detail-supplemental">{product.SupplementalName}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Small grape chip for card header
function GrapeChip({ state, labeled }) {
  if (labeled === null) return null;
  return (
    <span className={`grape-chip ${labeled ? 'grape-yes' : 'grape-no'}`}>
      🍇 {state} {labeled ? '✓' : '✗'}
    </span>
  );
}

// Detailed grape badge for expanded view
function GrapeDetailBadge({ state, labeled }) {
  if (labeled === null) return null;
  return (
    <div className={`grape-detail-badge ${labeled ? 'grape-badge-yes' : 'grape-badge-no'}`}>
      <span>{labeled ? '✓' : '✗'}</span>
      <span>
        {labeled
          ? `Labeled for grapes in ${state}`
          : `NOT labeled for grapes in ${state}`}
      </span>
    </div>
  );
}
